import api from './client';
import { API_ENDPOINTS } from './endpoints';

export type PayerType =
  | 'Commercial'
  | 'Medicare'
  | 'Medicaid'
  | 'Government'
  | 'Workers Compensation'
  | 'Third Party Administrator';

export type FilingLimitUnit = 'Days' | 'Months' | 'Years';

export type TimelyFilingStartFrom =
  | 'Date of Service'
  | 'Discharge Date'
  | 'Primary EOB Date'
  | 'Denial Date'
  | 'Payment Date';

export type DenialFilingStartFrom = 'Denial Date' | 'EOB Date' | 'Remittance Date' | 'Payment Date';

export type ClaimType = 'Professional' | 'Institutional' | 'Dental' | 'Vision' | 'Pharmacy';

export interface FilingLimit {
  value: number;
  unit: FilingLimitUnit;
}

export interface PayerTfiDetailsApiItem {
  _id: string;
  payer_name: string;
  payer_code: string;
  payer_type: PayerType;
  timely_filing_limit: FilingLimit;
  denial_filing_limit: FilingLimit;
  timely_filing_start_from: TimelyFilingStartFrom;
  denial_filing_start_from: DenialFilingStartFrom;
  claim_types: ClaimType[];
  requires_proof_of_timely_filing: boolean;
  active: boolean;
  notes?: string;
  meta?: { status?: string };
  createdAt?: string;
  updatedAt?: string;
}

export interface FailedPayerUploadRow {
  payer_name?: string | null;
  payer_code?: string | null;
  reason?: string;
}

export interface UploadPayersFileResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: {
    inserted_count?: number;
    failed_count?: number;
    failed_items?: FailedPayerUploadRow[];
  };
}

export interface PayersListResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: {
    payers?: PayerTfiDetailsApiItem[];
    count?: number;
  } | PayerTfiDetailsApiItem[];
}

export interface UpsertPayerPayload {
  id?: string;
  payer_name: string;
  payer_code: string;
  payer_type?: PayerType;
  timely_filing_limit: FilingLimit;
  denial_filing_limit: FilingLimit;
  timely_filing_start_from?: TimelyFilingStartFrom;
  denial_filing_start_from?: DenialFilingStartFrom;
  claim_types?: ClaimType[];
  requires_proof_of_timely_filing?: boolean;
  active?: boolean;
  notes?: string;
}

export interface PayerMutationResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data?: PayerTfiDetailsApiItem | null;
}

export const getPayers = async (params?: {
  page?: number;
  limit?: number;
  search?: string;
  payer_type?: PayerType;
  active?: 'true' | 'false';
}): Promise<PayersListResponse> => {
  return api.get<PayersListResponse>(API_ENDPOINTS.payerTfiDetails.list, { params });
};

export const upsertPayer = async (
  payload: UpsertPayerPayload,
): Promise<PayerMutationResponse> => {
  return api.post<PayerMutationResponse, UpsertPayerPayload>(
    API_ENDPOINTS.payerTfiDetails.upsert,
    payload,
  );
};

export const deletePayer = async (id: string): Promise<PayerMutationResponse> => {
  return api.delete<PayerMutationResponse>(
    `${API_ENDPOINTS.payerTfiDetails.list}/${id}`,
  );
};

export const uploadPayersFile = async (file: File): Promise<UploadPayersFileResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post<UploadPayersFileResponse, FormData>(API_ENDPOINTS.payerTfiDetails.uploadFile, formData);
};
